import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { PCacheable } from 'ts-cacheable';
import { StructureActionType } from '../../admin/structures/structure-action.type';
import { environment } from '../../environments/environment';
import { ApiService } from '../../shared/services/api.service';
import { DisplayLabel } from '../models/displayLabel';
import { DisplayStructure } from '../models/displayStructure';
import { StructureDto } from '../models/structure.dto';
import { Utils } from '../utils';

@Injectable({
  providedIn: 'root',
})
export class StructureService {
  private structures: DisplayStructure[] = [];
  public get displayStructures(): DisplayStructure[] {
    return this.structures;
  }
  private readonly structureAction: Subject<{ action: StructureActionType }> = new Subject<{
    action: StructureActionType;
  }>();
  public get structureAction$(): Observable<{ action: StructureActionType }> {
    return this.structureAction.asObservable();
  }
  constructor(private readonly api: ApiService) {}

  @PCacheable()
  public async findAll(sortColumn?: string, sortDesc?: boolean): Promise<DisplayStructure[]> {
    let url: string = environment.api.structures + (sortColumn ? '?sort=' + sortColumn + (sortDesc ? '&desc=true' : '') : '');
    this.structures = await this.api.get<DisplayStructure[]>(url);
    return this.structures;
  }

  @PCacheable()
  public async findAllInBeneficiarySupportArea(): Promise<DisplayStructure[]> {
    let url: string = environment.api.structures + '?benefSupport=true';
    this.structures = await this.api.get<DisplayStructure[]>(url);
    return this.structures;
  }

  @PCacheable()
  public async findAllByIdIodas(idIODAS: string): Promise<DisplayStructure[]> {
    return await this.api.get<DisplayStructure[]>(environment.api.structures + '?idIodas=' + idIODAS);
  }

  public async find(benefSupportOnly: boolean = false, structureId?: number): Promise<DisplayStructure[]> {
    const structures: DisplayStructure[] = benefSupportOnly ? await this.findAllInBeneficiarySupportArea() : await this.findAll();
    return structures.filter((structure: DisplayStructure) => !structureId || structure.id === structureId);
  }

  public async findById(id: number): Promise<StructureDto> {
    return await this.api.get<StructureDto>(environment.api.structures + '/' + id + '/details');
  }

  public async createStructure(structure: StructureDto): Promise<void> {
    await this.api.post(environment.api.structures, Utils.normalizeStrings(structure));
    this.structureAction.next({ action: 'created' });
  }

  public async updateStructure(structure: StructureDto): Promise<void> {
    const url: string = environment.api.structures + '/' + structure.id;
    await this.api.put(url, Utils.normalizeStrings(structure));
    this.structureAction.next({ action: 'updated' });
  }

  @PCacheable()
  public async findAllFocusAreas(): Promise<DisplayLabel[]> {
    return this.api.get<DisplayLabel[]>(environment.api.structures + '/focusAreas');
  }
}
